import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useVisitQueue } from '../hooks';
import { retryVisit, syncVisitQueue } from '../offline';
import { EmptyState, StatusPill, formatNzDateTime } from './Common';

export function SyncPanel({ ownerUserId, onClose }: { ownerUserId: string; onClose: () => void }) {
  const queue = useVisitQueue();
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: () => syncVisitQueue(ownerUserId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['visit-queue'] }),
        queryClient.invalidateQueries({ queryKey: ['locations'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
  const records = queue.data ?? [];
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sync-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">Offline queue</p>
            <h2 id="sync-title">Sync updates</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {records.length === 0 ? (
          <EmptyState title="Everything is synced">
            New offline visits will appear here until the server receives them.
          </EmptyState>
        ) : (
          <div className="queue-list">
            {records.map((record) => (
              <article className="queue-item" key={record.idempotencyKey}>
                <div>
                  <strong>{record.event.actionType.replaceAll('_', ' ')}</strong>
                  <span>{formatNzDateTime(record.event.clientOccurredAt)}</span>
                </div>
                <StatusPill
                  tone={
                    record.state === 'failed'
                      ? 'danger'
                      : record.state === 'syncing'
                        ? 'info'
                        : 'warning'
                  }
                >
                  {record.state}
                </StatusPill>
                {record.error ? <p className="queue-error">{record.error.message}</p> : null}
                {record.state === 'failed' ? (
                  <button
                    className="button button-small"
                    type="button"
                    onClick={async () => {
                      await retryVisit(record.idempotencyKey, ownerUserId);
                      await queryClient.invalidateQueries({ queryKey: ['visit-queue'] });
                    }}
                  >
                    Retry
                  </button>
                ) : null}
              </article>
            ))}
          </div>
        )}
        <button
          className="button button-primary button-full"
          type="button"
          onClick={() => sync.mutate()}
          disabled={!navigator.onLine || sync.isPending}
        >
          {sync.isPending ? 'Syncing…' : navigator.onLine ? 'Retry pending updates' : 'Offline'}
        </button>
      </section>
    </div>
  );
}
